import {
  useEffect,
  useState
} from 'react';
import {SupportTicketController} from '../../../../api/support/support-ticket.controller';
import {SupportTicketResolutionEnum} from '../../../../model/support/support-ticket-resolution.enum';
import {SupportTicketStatusEnum} from '../../../../model/support/support-ticket-status.enum';
import {SupportTicketModel} from '../../../../model/support/support-ticket.model';
import ResolveSupportTicketReq = SupportTicketController.ResolveSupportTicketReq;
import AnalyzeSupportTicketReq = SupportTicketController.AnalyzeSupportTicketReq;


type SupportTicketDrawerState = {
  ticket: SupportTicketModel
  isResolveButtonEnabled: boolean,
  isAnalyzeButtonEnabled: boolean,
  isDirtyState: boolean
  setTicket: (ticket: SupportTicketModel) => void
  onProcessedPressed: () => Promise<void>
  markTicketAsFixed: () => Promise<void>
  resolveTicket: () => Promise<void>
  analyzeTicket: () => Promise<void>
}

export const useSupportTicketDrawerState = (supportTicket: SupportTicketModel, refreshTickets: () => Promise<void>): SupportTicketDrawerState => {
  const [ticket, setTicket] = useState<SupportTicketModel>(supportTicket)
  const [isDirtyState, setDirtyState] = useState<boolean>(false)

  useEffect(() => {
    setTicket(supportTicket)
  }, [supportTicket])

  const isValuePresent = (value: string | null) => value !== null && value !== undefined && value !== ''

  const onProcessedPressed = async () => {
    await SupportTicketController.updateSupportTicketStatus(ticket.id, SupportTicketStatusEnum.PROCESSING, ticket.version)
    await refreshTickets()
    setDirtyState(false)
  }

  const markTicketAsFixed = async () => {
    await SupportTicketController.updateSupportTicketStatus(ticket.id, SupportTicketStatusEnum.RESOLVED, ticket.version)
    await refreshTickets()
    setDirtyState(false)
  }

  const resolveTicket = async () => {
    const status =
      ticket.resolution === SupportTicketResolutionEnum.FIX_SCHEDULED ? SupportTicketStatusEnum.IN_PROGRESS : SupportTicketStatusEnum.RESOLVED
    const body: ResolveSupportTicketReq = {
      resolution: ticket.resolution,
      message: ticket.message,
      jiraTicket: ticket.jiraTicket,
      estimatedFixDate: ticket.estimatedFixDate,
      status: status,
      classifiedType: ticket.classifiedType,
      classifiedErrorType: ticket.classifiedErrorType
    }
    await SupportTicketController.resolveSupportTicket(ticket.id, ticket.version, body)
    await refreshTickets()
    setDirtyState(false)
  }

  const analyzeTicket = async () => {
    const body: AnalyzeSupportTicketReq = {
      analyzeSummary: ticket.analyzeSummary,
      analyzeEstimatedCost: ticket.analyzeEstimatedCost,
      analyzeEstimatedDuration: ticket.analyzeEstimatedDuration,
    }
    await SupportTicketController.analyzeSupportTicket(ticket.id, ticket.version, body)
    await refreshTickets()
    setDirtyState(false)
  }

  const isClassificationValid= (ticket.classifiedType !== null && ticket.classifiedType === 'IMPROVEMENT' && ticket.classifiedErrorType === null)
    || (ticket.classifiedType !== null && ticket.classifiedType === 'ERROR' && ticket.classifiedErrorType !== null)

  return {
    ticket: ticket,
    isDirtyState: isDirtyState,
    isResolveButtonEnabled: ticket.resolution === 'FIX_SCHEDULED'
      ? (isDirtyState && isValuePresent(ticket.message) && isValuePresent(ticket.jiraTicket) && isValuePresent(ticket.estimatedFixDate) && isClassificationValid)
      : (isDirtyState && ticket.resolution !== null && isValuePresent(ticket.message) && isClassificationValid),
    isAnalyzeButtonEnabled: isValuePresent(ticket.analyzeSummary) && isValuePresent(`${ticket.analyzeEstimatedCost}`) && isValuePresent(`${ticket.analyzeEstimatedDuration}`),
    setTicket: (it) => {
      setTicket(it)
      setDirtyState(true)
    },
    onProcessedPressed: onProcessedPressed,
    markTicketAsFixed: markTicketAsFixed,
    resolveTicket: resolveTicket,
    analyzeTicket: analyzeTicket,
  }
}